import { memo } from 'react';
import { formatCurrency } from '../utils/calculations';
import { isAsset, getTypeLabel, getBalanceImpact } from '../utils/accountUtils';

// Phase 48.5: Memoize component to prevent unnecessary re-renders
function AccountBreakdown({ accounts }) {
  if (!accounts || accounts.length === 0) {
    return (
      <div className="bg-white dark:bg-slate-900 p-6 rounded-lg shadow-md text-center text-gray-500 dark:text-gray-400">
        No hay cuentas para mostrar
      </div>
    );
  }

  // Group accounts by type
  const accountsByType = accounts.reduce((acc, account) => {
    if (!acc[account.type]) {
      acc[account.type] = [];
    }
    acc[account.type].push(account);
    return acc;
  }, {});

  // Calculate net total balance
  const totalBalance = accounts.reduce((sum, acc) => {
    return sum + getBalanceImpact(acc.type, acc.balance);
  }, 0);

  return (
    <div className="bg-white dark:bg-slate-900 p-6 rounded-lg shadow-md">
      <h3 className="text-xl font-bold mb-4">Desglose por Cuenta</h3>

      <div className="space-y-4">
        {Object.entries(accountsByType).map(([type, accts]) => (
          <div key={type} className="space-y-2">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
              {getTypeLabel(type)}
            </h4>

            {accts.map((account) => {
              const impact = getBalanceImpact(account.type, account.balance);
              const isPositive = impact >= 0;
              const displayBalance = Math.abs(account.balance);

              return (
                <div key={account.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {account.name}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className={`text-sm font-semibold ${isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                        }`}>
                        {formatCurrency(displayBalance)}
                      </div>
                    </div>

                    {/* Visual bar relative to net total balance if possible, or relative to absolute sum */}
                    <div className="w-24 bg-gray-100 dark:bg-slate-800 rounded-full h-2 overflow-hidden">
                      <div
                        className={`h-full ${isPositive ? 'bg-green-500 dark:bg-green-600' : 'bg-red-500 dark:bg-red-600'}`}
                        style={{
                          width: `${Math.min(100, (Math.abs(account.balance) / Math.max(1, Math.abs(totalBalance))) * 100)}%`
                        }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}

        {/* Total section */}
        <div className="pt-4 border-t-2 border-gray-300 dark:border-slate-600">
          <div className="flex items-center justify-between">
            <div className="text-base font-bold text-gray-900 dark:text-gray-100">
              Balance Total
            </div>
            <div className={`text-lg font-bold ${totalBalance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
              }`}>
              {formatCurrency(totalBalance)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Phase 48.5: Export memoized component
export default memo(AccountBreakdown);
